import { AppError } from "./app-error";

// What the chat stream and a recording's stream share on the client: a wait that a signal can cut
// short, and reading an open server-sent stream until it should end.

/** Waits `ms`, or until `signal` is aborted. */
export function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener("abort", done, { once: true });
  });
}

/**
 * The frames of an open stream, until the caller leaves, the server ends it, or it has been open
 * too long. A change of person or workspace ends it with a failure that is not retried: asking
 * again would mint a token for the old scope and cancel the new one's.
 */
export async function* framesUntil(
  source: AsyncIterator<unknown>,
  caller: AbortSignal,
  scope: AbortSignal,
  maxMs: number,
): AsyncGenerator {
  const tooLong = new AbortController();
  const timer = setTimeout(() => {
    tooLong.abort();
  }, maxMs);
  const stop = AbortSignal.any([caller, scope, tooLong.signal]);
  const stopped = new Promise<IteratorResult<unknown>>((resolve) => {
    const end = () => {
      resolve({ done: true, value: undefined });
    };
    if (stop.aborted) end();
    else stop.addEventListener("abort", end, { once: true });
  });
  try {
    for (;;) {
      const next = source.next();
      // Closing the stream under a read that is waiting makes that read fail; nobody asked it.
      next.catch(() => {});
      const step = await Promise.race([next, stopped]);
      if (step.done) break;
      yield step.value;
    }
  } finally {
    clearTimeout(timer);
    await Promise.resolve(source.return?.()).catch(() => {});
  }
  if (scope.aborted) throw new AppError("ACCESS_CHANGED");
}
